(ns admin.events.activity-resources
  (:require [applied-science.js-interop :as j]
            [kyber.events.utils :refer [add-auth base-uri kyber-event-fx]]
            [kyber.lib.kyber-db :as kyber-db]
            [kyber.events.eql :as eql]
            [kyber.lib.kyber-eql :refer [defmutate]]
            [kyber.kyber-db.activity-resource :as activity-resource]))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; create-activity-resource
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-create-activity-resource-success
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn create-activity-resource-success
  [{:keys [db]}
   [event-name {:keys [on-success]} response]]
  (let [resource-uuid (:uuid response)]
    (cond-> {:db (assoc-in db [:activity-resources resource-uuid] response)
             :dispatch-n [[:analytics/save-datapoint {:event event-name
                                                      :event-version 0}]]}
      (some? on-success)
      (assoc :invoke on-success))))

(kyber-event-fx :activity-resources/create-activity-resource-success
                create-activity-resource-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-create-activity-resource-failure
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn create-activity-resource-failure
  [{:keys [db]} [event-name _]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:message (str "Failed to add resource. Please try again")
                  :status :error}]]})

(kyber-event-fx :activity-resources/create-activity-resource-failure
                create-activity-resource-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-create-activity-resource
    {:description ""
     :changelog
     [{:date "11OCT18"
       :event-version 0
       :data {}}]}))

(defn create-activity-resource
  [{:keys [db]}
   [event-name
    {:keys [activity-uuid curriculum-uuid] :as resource-link}]]
  {:db db
   :dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request
   {:name event-name
    :method :post
    :uri (base-uri db (str "/v1/admin/curricula/" curriculum-uuid
                           "/activities/" activity-uuid
                           "/resources"))
    :params (select-keys resource-link [:resource :learning-objective])
    :format :json
    :response-format :json
    :headers (add-auth db)
    :on-success [:activity-resources/create-activity-resource-success
                 (select-keys resource-link [:curriculum-uuid
                                             :activity-uuid
                                             :on-success])]
    :on-failure [:activity-resources/create-activity-resource-failure]}})

(kyber-event-fx :activity-resources/create-activity-resource
                create-activity-resource)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Note: Below are the prefered activity resource events. The above will be removed in
;;       the future when the legacy state management pattern has been removed.
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; get-activity-resource
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-get-activity-resource-success
    {:description ""
     :changelog
     [{:date "09JUL21"
       :event-version 0
       :data {}}]}))

(defn get-activity-resource-success
  [{:keys [db]} [_ props response]]
  (let [{:keys [on-resource-created redispatch-when wait activity]} props
        wait* (* 2 (or wait 1000))
        props* (assoc props :wait wait*)
        resource (:>/resource response)
        should-redispatch (boolean (redispatch-when props resource))]
    (cond-> {:db (kyber-db/tx-record db
                                     {:model :activity}
                                     (cond-> (assoc activity
                                                    :activity/resources
                                                    [resource])
                                       (contains? activity :activity/primary-learning-object)
                                       (assoc-in [:activity/primary-learning-object :activity/uuid] (:activity/uuid activity))))}
      (true? should-redispatch)
      (assoc :dispatch-later [{:ms wait*
                               :dispatch [::get-activity-resource props*]}])

      (and (false? should-redispatch)
           (fn? on-resource-created))
      (assoc :invoke #(on-resource-created resource)))))

(kyber-event-fx ::get-activity-resource-success get-activity-resource-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-activity-resource-failure
    {:description ""
     :changelog
     [{:date "09JUL21"
       :event-version 0
       :data {}}]}))

(defn get-activity-resource-failure
  [_ [event-name _ _]]
  {:dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]})

(kyber-event-fx ::get-activity-resource-failure get-activity-resource-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-get-activity-resource
    {:description ""
     :changelog
     [{:date "09JUL21"
       :event-version 0
       :data {}}]}))

(defn get-activity-resource
  [{:keys [db]} [event-name {:keys [activity is-plo-upload?] :as props}]]
  {:dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]]
   :request
   (let [activity-uuid (:activity/uuid activity)
         type-key (if is-plo-upload?
                    :activity/primary-learning-object
                    :activity/resource)]
     (eql/request
      db
      {:name event-name
       :params `[{(:>/resource ~(assoc (type-key activity) :activity/uuid activity-uuid))
                  [:activity/uuid
                   :resource/uuid
                   :resource/created-at
                   :resource/created-by
                   :resource/download-key
                   :resource/download-path
                   :resource/duration
                   :resource/description
                   :resource/failed?
                   :resource/file-name
                   :resource/file-size
                   :resource/file-type
                   {:resource/learning-objective
                    [:learning-objective/uuid
                     :learning-objective/description
                     :learning-objective/label]}
                   :resource/name
                   :resource/page-count
                   :resource/page-dimensions
                   :resource/processing?
                   :resource/source-key
                   :resource/source-path
                   :resource/status
                   :resource/url
                   :resource/m3u8
                   :resource/viewer-key
                   :resource/viewer-path]}]
       :on-success [::get-activity-resource-success props]
       :on-failure [::get-activity-resource-failure props]}))})

(kyber-event-fx ::get-activity-resource get-activity-resource)

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; upload-activity-resource
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-activity-resource-success
    {:description ""
     :changelog
     [{:date "09JUL21"
       :event-version 0
       :data {}}]}))

(defn upload-activity-resource-success
  [{:keys [db]} [event-name {:keys [is-plo-upload?] :as props} {activity 'activity/upload-resource}]]
  (let [type-key (if is-plo-upload?
                   :activity/primary-learning-object
                   :activity/resource)
        resource (get activity type-key)]
    {:db (kyber-db/tx-record db
                             {:model :activity}
                             (cond-> (assoc activity
                                            :activity/resources
                                            [(assoc resource
                                                    :activity/uuid (:activity/uuid activity))])
                               (contains? activity :activity/primary-learning-object)
                               (assoc-in [:activity/primary-learning-object :activity/uuid] (:activity/uuid activity))))
     :dispatch-n [[:analytics/save-datapoint {:event event-name
                                              :event-version 0}]
                  [:resources/upload-resource-to-s3
                   (assoc props
                          :presigned-url (:activity/presigned-url activity)
                          :on-success-handler :resources/upload-resource-to-s3-success
                          :dispatch-on-s3-upload-success ::get-activity-resource
                          :activity activity)]]}))

(kyber-event-fx ::upload-activity-resource-success upload-activity-resource-success)

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-activity-resource-failure
    {:description ""
     :changelog
     [{:date "09JUL21"
       :event-version 0
       :data {}}]}))

(defn upload-activity-resource-failure
  [_ [event-name _ _]]
  {:dispatch-n [[:analytics/save-datapoint {:event event-name
                                            :event-version 0}]
                [:notifier/create-toast
                 {:message (str "Failed to upload resource. Please try again")
                  :status :error}]]})

(kyber-event-fx ::upload-activity-resource-failure upload-activity-resource-failure)

(when ^boolean js/goog.DEBUG
  (def event-doc-upload-activity-resource
    {:description ""
     :changelog
     [{:date "09JUL21"
       :event-version 0
       :data {}}]}))

(defn upload-activity-resource
  [{:keys [db]} [event-name {:keys [upload-state activity-uuid is-plo-upload? resource-metadata]
                             :or {is-plo-upload? false}
                             :as raw-props}]]
  (let [type-key (if is-plo-upload?
                   :activity/primary-learning-object
                   :activity/resource)
        props (assoc raw-props
                     :redispatch-when
                     (if is-plo-upload?
                       #(true? (:resource/processing? %2))
                       #(nil? (:resource/viewer-key %2))))
        file (:original-file @upload-state)
        upload-has-not-begun (-> @upload-state :percent-uploaded zero?)
        content-disposition (or (@upload-state :content-disposition)
                                "attachment")
        custom-version-id (aget (clj->js resource-metadata) "custom-version-id")]
    (cond-> {:dispatch-n [[:analytics/save-datapoint {:event event-name
                                                      :event-version 0}]]}
      (and (some? file)
           (true? upload-has-not-begun))
      (assoc :request
             (eql/request
              db
              {:name event-name
               :params `[{(activity/upload-resource
                           {:activity/uuid ~activity-uuid
                            ~type-key
                            ~(cond-> {:resource/type (j/get file :type)
                                      :resource/content-disposition content-disposition
                                      :resource/file-name (j/get file :name)
                                      :resource/is-public false}
                               (some? custom-version-id)
                               (assoc :resource/custom-version-id custom-version-id))})
                          [:activity/uuid
                           :activity/presigned-url
                           {~type-key
                            [:resource/uuid]}]}]
               :on-success [::upload-activity-resource-success props]
               :on-failure [::upload-activity-resource-failure props]})))))

(kyber-event-fx ::upload-activity-resource upload-activity-resource)

(defmutate link-activity-resource
  {:model-key :activity
   :mutation-symbol 'activity/link-resource
   :event-namespace ::self
   :analytics-key :activity/link-resource
   :update-fn
   (fn [db _request {activity-uuid :activity/uuid :as resource}]
     (kyber-db/tx-record
      db
      {:model :activity}
      {:activity/uuid activity-uuid
       :activity/resources [resource]}))})

(defmutate unlink-activity-resource
  {:model-key :activity
   :mutation-symbol 'activity/unlink-resource
   :event-namespace ::self
   :analytics-key :activity/unlink-resource
   :update-fn
   (fn [db _request {activity-uuid :activity/uuid
                     resource-uuid :resource/uuid}]
     (kyber-db/retract-ref db
                           {:model :activity
                            :refs-key :activity/resources
                            :ref-ident-key (activity-resource/->ident-key {:resource/uuid resource-uuid
                                                                           :activity/uuid activity-uuid})}
                           {:activity/uuid activity-uuid}))})

(defmutate update-activity-resource
  {:model-key :activity
   :mutation-symbol 'resource/update
   :event-namespace ::self
   :analytics-key :activity/update-resource
   :update-fn
   (fn [db request resource]
     (kyber-db/tx-record
      db
      {:model :activity}
      {:activity/uuid (get-in request [:resource/activity :activity/uuid])
       :activity/resources [resource]}))})
